import { Box, Button, Pagination, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useItemWidth, useNotification } from "../../hooks";
import TestSuiteCard from "./TestSuiteCard";
import useDeleteTestCaseFromTestSuiteQuery from "../../useQuery/useDeleteTestCaseFromTestSuiteQuery";
import CustomDialog from "../../components-new/common/CustomDialog";
import { CustomSelect } from "../../components-new/common";
import {
  useAddToSuiteMutation,
  useMoveProductsQuery,
  useMoveSuiteQuery,
  useMoveTestCaseMutation,
  useActivateTCQuery,
} from "../../useQuery";
import { MoveIcon, PlusIcon } from "../../components-new/icons";
import useMoveModuleQuery from "../../useQuery/useMoveModuleQuery";
import { useQueryClient } from "@tanstack/react-query";

export default function TestSuiteCases({
  data,
  showTestCases,
  itemsPerRow,
  testSuiteId = null,
  setShowTestCases,
  selectedProdId,
  titleSuiteName,
  selectedModule,
  showDeletedTestCases,
  handleNestedSuiteClick,
  isSearched = false,
  onDeleteSuccess = null,
  isReadOnly = false,
}) {
  const containerRef = useRef(null);
  const gap = 16;
  const itemWidth = useItemWidth(containerRef, itemsPerRow, gap);
  const queryClient = useQueryClient();
  const notify = useNotification();
  const [testSuitePage, setTestSuitePage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddTestCaseModal, setShowAddTestCaseModal] = useState(false);
  const [caseToBeDeleted, setCaseToBeDeleted] = useState(null);
  const [casesToBeMoved, setCasesToBeMoved] = useState([]);

  const [testSuiteData, setTestSuiteData] = useState([]);
  const [moveProdID, setMoveProdId] = useState(null);
  const [moveModId, setMoveModId] = useState(null);
  const [moveSuiteId, setMoveSuiteId] = useState(null);
  const [caseToBeAdded, setCaseToBeAdded] = useState([]);
  const [title, setTitle] = useState("");

  const [selectAll, setSelectAll] = useState(false);

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const allVisible = (data || []).filter(
        (suite) => suite.idtest_suite != null || suite.idtest_case != null,
      );
      setCaseToBeAdded(allVisible);
    } else {
      setCaseToBeAdded([]);
    }
  };

  const { data: products } = useMoveProductsQuery();
  const { data: modules } = useMoveModuleQuery(moveProdID);
  const deleteTestCaseWithCallback = useDeleteTestCaseFromTestSuiteQuery({
    onSuccess: () => {
      if (isSearched && onDeleteSuccess) {
        onDeleteSuccess();
      }
    },
  });

  const { mutate: activateTC } = useActivateTCQuery({
    onSuccess: (response) => {
      // Backend response is in response.data
      const message = response?.data;

      // If backend returns a plain string "Test Case Restored"
      if (message === "Test Case Restored") {
        notify("Test case restored successfully", "success");
      }
      // If backend might also sometimes return an object with a message field,
      // handle that as well:
      else if (message?.message === "success" || message?.message === "activated") {
        notify("Test case restored successfully", "success");
      } else if (message?.message === "already_active") {
        notify("Test case is already active", "info");
      } else if (message?.message === "not_found") {
        notify("Test case not found", "error");
      } else {
        notify("Error restoring test case", "error");
      }

      // Refresh suite test case list
      queryClient.refetchQueries({
        queryKey: ["testCasesInSuite"],
        exact: false,
      });

      queryClient.refetchQueries({
        queryKey: ["searchTCDropDown"],
        exact: false,
      });
    },
    onError: () => {
      notify("Error restoring test case", "error");
    },
  });

  const { data: testSuits, refetch } = useMoveSuiteQuery(moveProdID, !!moveProdID);
  const { mutate: postTestCase } = useAddToSuiteMutation({
    onSuccess: (_, variables) => {
      const isSuite = variables?.data?.[0]?.list || variables?.data?.[0]?.idtest_suite;
      notify(isSuite ? "Test Suite added successfully" : "Test Case added successfully", "success");

      queryClient.refetchQueries({
        queryKey: ["testSuiteNew"],
        exact: false,
      });
      queryClient.refetchQueries({
        queryKey: ["testCasesInSuite"],
        exact: false,
      });
      refetch();
    },
  });
  const { mutate: moveTestCase } = useMoveTestCaseMutation();

  useEffect(() => {
    if (testSuits) {
      setTestSuiteData(testSuits.flat());
    }
  }, [testSuits]);

  useEffect(() => {
    setSelectAll(false);
    setCaseToBeAdded([]);
  }, [data]);
  const handleDeleteClick = (e, suite) => {
    e.stopPropagation();
    setShowDeleteModal(true);
    setCaseToBeDeleted([suite]);
  };

  const handleMoveTestCase = (e, suite) => {
    e.stopPropagation();
  };

  const handleAddTestCase = (e, suite) => {
    e.stopPropagation();
    setShowAddTestCaseModal(true);
    setCasesToBeMoved((prevCases) => [...prevCases, suite]);
  };

  const getSelectionKey = (testCase) => {
    const entityType = testCase?.list ? "suite" : "case";
    const entityId = testCase?.idtest_case ?? testCase?.idtest_suite;
    const objectIndex = testCase?.Object_Index ?? testCase?.TestcaseIndex;

    if (entityId == null) return null;

    if (objectIndex != null) {
      return `${entityType}-${entityId}-${objectIndex}`;
    }

    return `${entityType}-${entityId}`;
  };

  const getCardKey = (suite, index) => {
    const entityType = suite?.list ? "suite" : "case";
    const entityId = suite?.idtest_case ?? suite?.idtest_suite;
    const objectIndex = suite?.Object_Index ?? suite?.TestcaseIndex;

    if (entityId != null && objectIndex != null) {
      return `${entityType}-${entityId}-${objectIndex}`;
    }

    if (entityId != null) {
      return `${entityType}-${entityId}-${index}`;
    }

    return `${entityType}-${index}`;
  };

  const addTestCase = (e, testCase) => {
    const selectionKey = getSelectionKey(testCase);
    if (!selectionKey) return;

    if (e.target.checked) {
      setCaseToBeAdded((prev) =>
        prev.some((item) => getSelectionKey(item) === selectionKey) ? prev : [...prev, testCase],
      );
    } else {
      setCaseToBeAdded((prev) => prev.filter((item) => getSelectionKey(item) !== selectionKey));
    }
  };

  const DeleteAction = () => {
    const isRestore = showDeletedTestCases;
    const handleConfirm = () => {
      if (!caseToBeDeleted || !caseToBeDeleted[0]) return;

      if (isRestore) {
        // RESTORE / UNDELETE
        const testCaseId = caseToBeDeleted[0].idtest_case;
        activateTC({ id: testCaseId });
      } else {
        const testCaseIndex = caseToBeDeleted[0].Object_Index ?? caseToBeDeleted[0].TestcaseIndex;

        deleteTestCaseWithCallback.mutate({
          id: testSuiteId,
          selectedcases: caseToBeDeleted,
          index: testCaseIndex,
          isSearchMode: isSearched,
        });
      }

      setShowDeleteModal(false);
      setCaseToBeDeleted(null);
    };
    return (
      <>
        <Button
          sx={{ color: "#ff5336" }}
          onClick={() => {
            setCaseToBeDeleted(null);
            setShowDeleteModal(false);
          }}
        >
          Cancel
        </Button>
        <Button variant="contained" sx={{ background: "#ff5336" }} onClick={handleConfirm}>
          {isRestore ? "Restore" : "Delete"}
        </Button>
      </>
    );
  };

  const AddTestCaseAction = ({ caseToBeAdded, setCaseToBeAdded }) => {
    const resetAddModal = () => {
      setShowAddTestCaseModal(false);
      setMoveProdId(null);
      setMoveSuiteId(null);
      setMoveModId(null);
      setCaseToBeAdded([]);
      setSelectAll(false);
    };

    return (
      <>
        <Button sx={{ color: "#ff5336" }} onClick={resetAddModal}>
          Cancel
        </Button>
        <Button
          disabled={
            title === "Add Test Case" ? !(moveProdID && moveSuiteId) : !(moveProdID && moveModId)
          }
          variant="contained"
          sx={{ background: "#ff5336" }}
          onClick={() => {
            if (title === "Add Test Case") {
              postTestCase({
                data: caseToBeAdded,
                params: {
                  idtest_suite: moveSuiteId,
                  pnum: 1,
                },
              });
              resetAddModal();
            }
            if (title === "Move Test Case") {
              moveTestCase({
                data: caseToBeAdded,
                params: {
                  modid: moveModId,
                },
              });
              resetAddModal();
            }
          }}
        >
          Save
        </Button>
      </>
    );
  };

  return (
    <>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        rowGap={2}
        mb={2}
      >
        <Box display="flex" flexWrap="wrap">
          <Typography variant="h6">
            {showDeletedTestCases ? "All Deleted Test Cases for " : "All Test Cases for "}
            {testSuiteId && titleSuiteName ? titleSuiteName : selectedModule || ""}
          </Typography>
        </Box>
        {!showDeletedTestCases && (
          <Box display="flex" gap={2} flexWrap="wrap" ml="auto">
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                border: "1px solid #5541d7",
                borderRadius: "8px",
                px: 1.5,
                py: 0.8,
                background: "#eeedfe",
                cursor: isReadOnly ? "not-allowed" : "pointer",
                opacity: isReadOnly ? 0.5 : 1,
              }}
              onClick={() => {
                if (isReadOnly) return;
                handleSelectAll(!selectAll);
              }}
            >
              <input
                type="checkbox"
                checked={selectAll}
                disabled={isReadOnly}
                onChange={(e) => handleSelectAll(e.target.checked)}
                style={{
                  width: 14,
                  height: 14,
                  accentColor: "#5541d7",
                  cursor: isReadOnly ? "not-allowed" : "pointer",
                }}
              />
              <Typography fontSize={13} color="#3c3489" fontWeight={500}>
                Select All (
                {(data || []).filter((s) => s.idtest_suite != null || s.idtest_case != null).length}
                )
              </Typography>
            </Box>
            <Button
              sx={{ textTransform: "capitalize", p: "10px 15px" }}
              endIcon={<PlusIcon />}
              variant="contained"
              onClick={() => {
                setTitle("Add Test Case");
                setShowAddTestCaseModal(true);
              }}
              disabled={!caseToBeAdded.length}
            >
              Add to Test Suite
            </Button>

            <Button
              sx={{ textTransform: "capitalize", p: "10px 15px" }}
              endIcon={<MoveIcon />}
              variant="contained"
              onClick={() => {
                setTitle("Move Test Case");
                setShowAddTestCaseModal(true);
              }}
              disabled={!caseToBeAdded.length}
            >
              Move Test Case Module
            </Button>
          </Box>
        )}
        ;
      </Box>

      {/* Dialogs */}
      <CustomDialog
        title={
          showDeletedTestCases
            ? "Are you sure you want to restore this Test Case?"
            : caseToBeDeleted?.[0]?.list
              ? "Are you sure you want to remove this Test Suite from the parent Test Suite?"
              : "Are you sure you want to remove this Test Case from the Test Suite?"
        }
        open={showDeleteModal}
        actions={<DeleteAction isRestore={showDeletedTestCases} />}
      />

      <CustomDialog
        title={title}
        open={showAddTestCaseModal}
        actions={
          <AddTestCaseAction
            casesToBeMoved={casesToBeMoved}
            caseToBeAdded={caseToBeAdded}
            setCaseToBeAdded={setCaseToBeAdded}
          />
        }
        width="unset"
        content={
          <Box display="flex" flexWrap="wrap" gap={2}>
            <CustomSelect
              label="Products"
              options={(Array.isArray(products) ? products : []).map((p) => ({
                label: p.productname,
                value: p.idproducts,
              }))}
              width="230px"
              background="#ffffff"
              value={moveProdID || ""}
              onChange={(e) => {
                setMoveProdId(e.target.value);
                setMoveSuiteId(null);
              }}
            />

            {title === "Add Test Case" && (
              <>
                {Array.isArray(testSuiteData) && testSuiteData.length > 0 ? (
                  <CustomSelect
                    label="Suite"
                    options={testSuiteData.map((s) => ({
                      label: s.Test_Suite,
                      value: s.idtest_suite,
                    }))}
                    width="230px"
                    background="#ffffff"
                    value={moveSuiteId || ""}
                    onChange={(e) => setMoveSuiteId(e.target.value)}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {(() => {
                      const selectedProduct = (Array.isArray(products) ? products : []).find(
                        (p) => String(p.idproducts) === String(moveProdID),
                      );

                      const name = selectedProduct?.productname || "This product";

                      return `${name} has no test suites yet.`;
                    })()}
                  </Typography>
                )}
              </>
            )}

            {title === "Move Test Case" && (
              <CustomSelect
                label="Module"
                options={(Array.isArray(modules) ? modules : []).map((m) => ({
                  label: m.modulename,
                  value: m.idmodules,
                }))}
                width="230px"
                background="#ffffff"
                value={moveModId || ""}
                onChange={(e) => setMoveModId(e.target.value)}
              />
            )}
          </Box>
        }
      />

      {/* Cards Grid */}
      <Box
        width="100%"
        py={2}
        px={1}
        display="grid"
        gridTemplateColumns="repeat(auto-fill, minmax(260px, 1fr))"
        gap="24px"
      >
        {data?.length > 0 ? (
          data
            .filter(
              (suite) =>
                // filtering out invalid objects - must have either idtest_suite or idtest_case ( backend is sending some additional data, filtering that out. )
                suite.idtest_suite != null || suite.idtest_case != null,
            )
            .map((suite, index) =>
              suite.list ? (
                <TestSuiteCard
                  key={getCardKey(suite, index)}
                  item={suite}
                  isFolder={true}
                  isSuiteInsideSuite={true}
                  handleSuiteClick={() =>
                    handleNestedSuiteClick(suite.idtest_suite, suite.Test_Suite)
                  }
                  handleDeleteClick={(e) => handleDeleteClick(e, suite)}
                  handleSettings={() => setShowTestCases(false)}
                  handleReNameSuite={() => setShowTestCases(false)}
                  addTestCase={(e) => addTestCase(e, suite)}
                  getSelectionKey={getSelectionKey}
                  suiteTitle
                  caseToBeAdded={caseToBeAdded}
                  selectedProdId={selectedProdId}
                  isReadOnly={isReadOnly}
                />
              ) : (
                <TestSuiteCard
                  key={getCardKey(suite, index)}
                  item={suite}
                  isFolder={false}
                  testSuiteId={testSuiteId}
                  handleDeleteClick={(e) => handleDeleteClick(e, suite)}
                  handleMoveTestCase={(e) => handleMoveTestCase(e, suite)}
                  handleAddTestCase={(e) => handleAddTestCase(e, suite)}
                  addTestCase={(e) => addTestCase(e, suite)}
                  getSelectionKey={getSelectionKey}
                  caseToBeAdded={caseToBeAdded}
                  selectedProdId={selectedProdId}
                  showDeletedTestCases={showDeletedTestCases}
                  handleRestoreClick={(e) => handleDeleteClick(e, suite)}
                  isReadOnly={isReadOnly}
                />
              ),
            )
        ) : (
          <Box>No Test Case Available</Box>
        )}
      </Box>
    </>
  );
}
